import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ContainerSasUri, ErrorHandlingMode, LoaderHandlingMode, Notifica } from '@shared/models';
import { NotifyUserService } from '@shared/notify-user.service';
import { DettaglioF24 } from 'app/main/cassetto-fiscale-folder/cassetto-fiscale.models';
import { TelematicoData } from 'app/main/emissioni-folder/emissioni-models';
import { TipoScadenza } from 'app/main/gestione-scadenze-folder/gestione-scadenze.models';
import { ForfettappApiClient, ForfettappApiClientOptions } from 'app/shared/forfettapp-api-client';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
    Dashboard,
    DataToSendDichiarazione,
    DettagliAccontiVersatiModel,
    DettagliContributiVersatiModel,
    DettagliQuadroRsModel,
    DichiarazioneModel,
    DichiarazioneModelUnicoData,
    NotificaRicalcoloSecondoAccontoPayload,
    NotificaRicalcoloSecondoAccontoSceltaGestione,
    RitenuteCertificateDetail,
    StatoDichiarazione,
    TotalePerFornitore,
    VersamentiAltreContribuzioniModel,
} from '../dischiarazione.models';
import { FamiliariACarico } from '../visualizza-familiari-a-carico/visualizza-familiari-a-carico.models';
import { DettagliQuadroLm45Component } from '../visualizza-quadro-lm/dettagli-quadro-lm-45/dettagli-quadro-lm-45.component';
import { RedditiDaTerreni } from '../visualizza-quadro-ra/visualizza-quadro-ra.models';
import { RedditiDaFabbricati } from '../visualizza-quadro-rb/visualizza-quadro-rb.models';
import { RedditiDaDipendente } from '../visualizza-quadro-rc/visualizza-quadro-rc.models';
import { OneriESpese } from '../visualizza-quadro-rp/visualizza-quadro-rp.models';
import { DettagliContributiVersatiDaF24Component } from '../visualizza-quadro-rr-sezione1/dettagli-contributi-versati-da-f24/dettagli-contributi-versati-da-f24.component';
import { DettagliContributiVersatiComponent } from '../visualizza-quadro-rr-sezione1/dettagli-contributi-versati/dettagli-contributi-versati.component';
import { DettagliCreditoAnnoPrecCompensatoModF24Component } from '../visualizza-quadro-rr-sezione1/dettagli-credito-anno-prec-compensato-mod-F24/dettagli-credito-anno-prec-compensato-mod-F24.component';
import { DettagliAccontoVersatoComponent } from '../visualizza-quadro-rr-sezione2/dettagli-acconto-versato/dettagli-acconto-versato.component';
import { DettagliQuadroRsComponent } from '../visualizza-quadro-rs/dettagli-quadro-rs/dettagli-quadro-rs.component';
import { RedditoFinanziarioEstero } from '../visualizza-quadro-rw/visualizza-quadro-rw.models';

export interface IdentifyPayload {
    forfettario: number;
    year: number;
}

@Injectable({ providedIn: 'root' })
export class DichiarazioneService {
    private _dataToSendDichiarazione = new BehaviorSubject<DataToSendDichiarazione>(null);
    private _refreshDichiarazioneDeiRedditi = new Subject<void>();
    private _quadroSalvato = new Subject<void>();

    private _dichiarazioneSasToken = new BehaviorSubject<ContainerSasUri>(null);

    private commonDialogConfig = new MatDialogConfig();

    constructor(
        private service: ForfettappApiClient,
        private dialog: MatDialog,
        private notifyUserService: NotifyUserService,
    ) {
        this.commonDialogConfig.width = 'auto';
        this.commonDialogConfig.height = 'auto';
        this.commonDialogConfig.maxWidth = '50%';
        this.commonDialogConfig.panelClass = 'toolbar-dialog';
    }

    get dataToSendDichiarazione$() {
        return this._dataToSendDichiarazione.asObservable().pipe(
            filter(x => Boolean(x)),
            shareReplay(1),
        );
    }

    get dataToSendDichiarazione() {
        return this._dataToSendDichiarazione.value;
    }

    get dichiarazioneSasToken() {
        return this._dichiarazioneSasToken.value;
    }

    get dichiarazioneSasToken$() {
        return this._dichiarazioneSasToken.asObservable().pipe(filter(containerSasUri => Boolean(containerSasUri)));
    }

    get refreshDichiarazioneDeiRedditi$() {
        return this._refreshDichiarazioneDeiRedditi.asObservable();
    }

    get quadroSalvato$() {
        return this._quadroSalvato.asObservable();
    }

    set dataToSendDichiarazione(data: DataToSendDichiarazione) {
        this._dataToSendDichiarazione.next(data);
    }

    set dichiarazioneSasToken(token: ContainerSasUri) {
        this._dichiarazioneSasToken.next(token);
    }

    refreshDichiarazioneDeiRedditi() {
        this._refreshDichiarazioneDeiRedditi.next();
    }

    notificaQuadroSalvato() {
        this._quadroSalvato.next();
    }

    getDichiarazioneRedditi(
        forfettariId: number,
        anno: number,
        clientOptions?: ForfettappApiClientOptions,
        errorHandling = ErrorHandlingMode.useGlobalErrorHandler,
    ) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service.get<DichiarazioneModel>({
            url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}`,
            headers: header,
            errorHandling,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    getDichiarazioneRedditiUnicoData(
        forfettariId: number,
        anno: number,
        clientOptions?: ForfettappApiClientOptions,
        errorHandling = ErrorHandlingMode.useGlobalErrorHandler,
    ) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service.get<DichiarazioneModelUnicoData>({
            url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/unico-data`,
            headers: header,
            errorHandling,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    getPdfProvvisorioDichiarazione(forfettariId: number, anno: number) {
        var params = new HttpParams().append('v', Date.now().toString()); // Per evitare la cache del browser
        return this.service
            .get<ArrayBuffer>({
                url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/pdf`,
                params,
                loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
                errorHandling: ErrorHandlingMode.useLocalErrorHandler,
                responseType: 'arraybuffer',
            })
            .pipe(
                map(response => new Blob([response], { type: 'application/pdf' })),
                catchError(() => {
                    this.notifyUserService.showErrorModal({ erroreHtml: 'Documento non disponibile. Riprova più tardi.', messaggi: [] });
                    return of(<Blob>null);
                }),
            );
    }

    getPdfPrecompilata(forfettariId: number, anno: number): Observable<{ fileName: string; blob: Blob }> {
        var params = new HttpParams().append('v', Date.now().toString()); // Per evitare la cache del browser
        return this.service
            .get<HttpResponse<ArrayBuffer>>({
                observe: 'response',
                url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/precompilata/pdf`,
                params,
                errorHandling: ErrorHandlingMode.useLocalErrorHandler,
                loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
                responseType: 'blob' as 'json',
            })
            .pipe(
                map(response => {
                    let fileName = `Precompilata_${anno}.pdf`;
                    const contentDisposition = response.headers.get('content-disposition');
                    if (contentDisposition) {
                        fileName = contentDisposition.split(';')[1].split('=')[1].replace(/\"/g, '');
                    }
                    const blob = new Blob([response.body], { type: 'application/pdf' });
                    return { fileName, blob };
                }),
                catchError(() => {
                    this.notifyUserService.showErrorModal({ erroreHtml: 'Documento pdf non disponibile. Riprova più tardi.', messaggi: [] });
                    return of(null);
                }),
            );
    }

    getCertificazioniUnichePdf(forfettariId: number, anno: number): Observable<{ fileName: string; blob: Blob }> {
        var params = new HttpParams().append('v', Date.now().toString());
        return this.service
            .get<HttpResponse<ArrayBuffer>>({
                observe: 'response',
                url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/precompilata/certificazioni-uniche-pdf`,
                params,
                errorHandling: ErrorHandlingMode.useLocalErrorHandler,
                loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
                responseType: 'blob' as 'json',
            })
            .pipe(
                map(response => {
                    let fileName = `CU_${anno}.zip`;
                    const contentDisposition = response.headers.get('content-disposition');
                    if (contentDisposition) {
                        fileName = contentDisposition.split(';')[1].split('=')[1].replace(/\"/g, '');
                    }
                    const contentType = response.headers.get('content-type') || 'application/zip';
                    const blob = new Blob([response.body], { type: contentType });
                    return { fileName, blob };
                }),
                catchError(() => {
                    this.notifyUserService.showErrorModal({ erroreHtml: 'Certificazioni Uniche non disponibili.', messaggi: [] });
                    return of(null);
                }),
            );
    }

    getDashboard(utenteId: number, anno: number, clientOptions?: ForfettappApiClientOptions) {
        var headers = new HttpHeaders();
        if (clientOptions?.cacheData) headers = headers.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) headers = headers.set('refresh-data', String(clientOptions.refreshData));

        const params = new HttpParams().set('anno', anno.toString());
        return this.service.get<Dashboard>({
            url: `/backoffice/forfettari/${utenteId}/dashboard`,
            params,
            headers,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    riapriNotificaRicalcoloSecondoAcconto(utenteId: number, anno: number) {
        const params = new HttpParams().set('annoRedditi', anno.toString()).set('utentiId', utenteId.toString());

        return this.service.post({
            url: `/backoffice/dichiarazioni/TEMP/riapri-notifica-ricalcolo-secondo-acconto`,
            params,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    riapriNotificaScaricoPreliminarePrecompilata(utenteId: number, anno: number) {
        const params = new HttpParams().set('annoRedditi', anno.toString()).set('utentiId', utenteId.toString());

        return this.service.post({
            url: `/backoffice/dichiarazioni/forfettari/${utenteId}/dichiarazione/${anno}/precompilata/commands/riapri-notifica-scarico-preliminare`,
            params,
            errorHandling: ErrorHandlingMode.useLocalErrorHandler,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    creaNotificaScaricoPreliminarePrecompilata(utenteId: number, anno: number) {
        const params = new HttpParams().set('annoRedditi', anno.toString()).set('utentiId', utenteId.toString());

        return this.service.post({
            url: `/backoffice/dichiarazioni/forfettari/${utenteId}/dichiarazione/${anno}/precompilata/commands/crea-notifica-scarico-preliminare-precompilata`,
            params,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
        });
    }

    getContributiPagatiTramiteF24(codiceFiscale: string, anno: number, categoriaTributo: number, perAnnoDataCompetenza: boolean) {
        const perAnnoPath = perAnnoDataCompetenza ? 'per-anno-competenza' : 'per-anno-versamento';
        var params = new HttpParams();
        if (categoriaTributo) params = params.set('categoriaTributo', categoriaTributo.toString());
        return this.service
            .get<DettaglioF24[]>(`/backoffice/dichiarazioni/contribuenti/${codiceFiscale}/anno/${anno}/pagamenti-per-categoria-tributo-${perAnnoPath}`, params)
            .pipe(catchError(() => of(<DettaglioF24[]>[])));
    }

    getDettagliContributiVersatiAltreContribuzioni(forfettariId: number, anno: number) {
        return this.service.get<VersamentiAltreContribuzioniModel[]>(
            `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-contributi-versati-altre-contribuzioni`,
        );
    }

    getDettagliSingoloQuadroRs(forfettariId: number, anno: number, rigoRsId: number) {
        return this.service.get<DettagliQuadroRsModel>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-quadri-rs/${rigoRsId}`);
    }

    getNotificaRicalcoloSecondoAcconto(forfettariId: number, anno: number) {
        return this.service.get<Notifica<NotificaRicalcoloSecondoAccontoPayload, NotificaRicalcoloSecondoAccontoSceltaGestione>>({
            url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/notifica-ricalcolo-secondo-acconto`,
        });
    }

    upsertDettagliSingoloQuadroRs(forfettariId: number, anno: number, rigoRsId: number, importo: number) {
        return this.service.post<DettagliQuadroRsModel>(
            `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-quadri-rs/${rigoRsId}/importo-manuale/${importo}`,
        );
    }

    getDettagliQuadriRs(forfettariId: number, anno: number, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service
            .get<DettagliQuadroRsModel[]>({
                url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-quadri-rs`,
                headers: header,
            })
            .pipe(catchError(() => of(<DettagliQuadroRsModel[]>null)));
    }

    getTotaliDocumentiPassiviPerClassificazioneCostiId(
        forfettariId: number,
        anno: number,
        classificazioneCostiId: number,
        clientOptions?: ForfettappApiClientOptions,
    ) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service
            .get<TotalePerFornitore[]>({
                url: `/backoffice/documenti-passivi/utente/${forfettariId}/anno/${anno}/documenti-passivi/classificazioni-costi/${classificazioneCostiId}`,
                headers: header,
            })
            .pipe(catchError(() => of(<TotalePerFornitore[]>null)));
    }

    upsertDettagliContributiVersatiAltreContribuzioni(forfettariId: number, anno: number, dettaglio: VersamentiAltreContribuzioniModel) {
        return this.service.post<VersamentiAltreContribuzioniModel>(
            `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-contributi-versati-altre-contribuzioni`,
            dettaglio,
        );
    }

    confermaPrecompilataAltriRedditi(forfettariId: number, anno: number) {
        return this.service
            .post(
                `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/conferma-precompilata`,
                {},
                null,
                ErrorHandlingMode.useLocalErrorHandler,
            )
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    creaOrdineDichiarazione(forfettariId: number, anno: number) {
        return this.service.post(`/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/crea-ordine-dichiarazione`);
    }

    getRitenuteCertificate(forfettarioId: number, anno: number): Observable<RitenuteCertificateDetail> {
        return this.service.get<RitenuteCertificateDetail>(`/backoffice/dichiarazioni/forfettari/${forfettarioId}/dichiarazione/${anno}/ritenute-certificate`);
    }

    uploadRitenuteCertificate(
        voci: { mese: number; importo: number; documentoId?: string }[],
        files: File[],
        forfettarioId: number,
        anno: number
    ) {
        const formData = new FormData();
        formData.append('voci', JSON.stringify(voci));
        files.forEach(file => formData.append('files', file));
        return this.service
            .post(`/backoffice/dichiarazioni/forfettari/${forfettarioId}/dichiarazione/${anno}/ritenute-certificate`, formData)
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    toggleClienteExDatoreDiLavoro(forfettariId: number, annoRedditi: number, isExDatoreDiLavoro: boolean) {
        return this.service.post<DichiarazioneModel>(
            `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${annoRedditi}/toggle-cliente-ex-datore-di-lavoro`,
            { isExDatoreDiLavoro },
        );
    }

    deleteCertificazioneRitenute(utenteId: number, anno: number) {
        return this.service
            .delete(`/backoffice/dichiarazioni/forfettari/${utenteId}/dichiarazione/${anno}/ritenute-certificate`)
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    deleteDettagliContributiVersatiAltreContribuzioni(forfettariId: number, anno: number, dettaglioId: number): Observable<void> {
        return this.service
            .delete<void>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-contributi-versati-altre-contribuzioni/${dettaglioId}`)
            .pipe(
                switchMap(() => this.getDichiarazioneRedditiUnicoData(forfettariId, anno, { refreshData: true })),
                tap(() => this.notificaQuadroSalvato()),
                map(() => void 0),
            );
    }

    ricalcolaDichiarazione(utenteId: number, annoRedditi: number) {
        return this.service.post<DichiarazioneModel>(
            `/backoffice/dichiarazioni/forfettari/${utenteId}/anno/${annoRedditi}/reimposta-parametri-ricalcola-dichiarazione`,
        );
    }

    resetSceltaEnasarco(utentiId: number, anno: number) {
        const params = new HttpParams().set('anno', anno.toString());
        return this.service
            .post(`/backoffice/forfettari/${utentiId}/reset-scelta-enasarco`, null, params)
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    getDettagliContributiVersati(forfettariId: number, anno: number) {
        return this.service.get<DettagliContributiVersatiModel>(
            `/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-contributi-versati`,
        );
    }

    getTotaleContributiEnasarcoVersati(forfettariId: number, anno: number) {
        return this.service.get<number>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/totale-contributi-enasarco-versati`);
    }

    getDettagliAccontiVersati(forfettariId: number, anno: number) {
        return this.service.get<DettagliAccontiVersatiModel>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/anno/${anno}/dettagli-acconti-versati`);
    }

    getAnnotazioni(forfettariId: number, anno: number, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service.get<string>({
            url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/annotazioni`,
            headers: header,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
            responseType: 'text',
        });
    }

    getAtecoCorrelazioni(forfettariId: number) {
        return this.service.get<{ ateco2025: string; ateco2022Correlato: string }[]>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/ateco-correlazioni`);
    }

    getAnnotazioniDelSistema(forfettariId: number, anno: number, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service.get<string>({
            url: `/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/annotazioni-del-sistema`,
            headers: header,
            loaderHandling: LoaderHandlingMode.useLocalLoaderHandler,
            responseType: 'text',
        });
    }

    getTokenContainer() {
        return this.service
            .get<ContainerSasUri>({
                url: `/backoffice/dichiarazioni/sas-token`,
            })
            .pipe(tap(token => (this.dichiarazioneSasToken = token)));
    }

    upsertAnnotazioni(forfettariId: number, anno: number, annotazioni: string) {
        var params = new HttpParams().append('annotazioni', annotazioni);
        return this.service.post<Response>(`/backoffice/dichiarazioni/forfettari/${forfettariId}/dichiarazione/${anno}/annotazioni`, {}, params);
    }

    escludiIncludiRigaDaTelematicoDaImportare({
        utentiId,
        anno,
        codiceTributo,
        annoTributo,
        escluso,
        importoDaAggiornare,
        tipoScadenza,
    }: {
        utentiId: number;
        anno: number;
        codiceTributo: string;
        annoTributo: number;
        escluso: boolean;
        importoDaAggiornare?: number;
        tipoScadenza?: TipoScadenza;
    }) {
        let httpParams = new HttpParams();

        if (escluso) httpParams = httpParams.set('escluso', escluso.toString());
        if (importoDaAggiornare) httpParams = httpParams.set('importoDaAggiornare', importoDaAggiornare.toString());
        if (tipoScadenza) httpParams = httpParams.set('tipoScadenza', tipoScadenza.toString());

        return this.service.post<TelematicoData>(
            `/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/unico-telematico/${codiceTributo}/${annoTributo}`,
            null,
            httpParams,
        );
    }

    getValidazioneUnicoTelematico({
        utentiId,
        anno,
        clientOptions,
        loaderHandling = LoaderHandlingMode.useGlobalLoaderHandler,
    }: {
        utentiId: number;
        anno: number;
        clientOptions?: ForfettappApiClientOptions;
        loaderHandling?: LoaderHandlingMode;
    }) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service.get<string>({
            url: `/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/unico-telematico/validazione`,
            headers: header,
            responseType: 'text',
            loaderHandling,
        });
    }

    upsertRedditiQuadroRa(utentiId: number, anno: number, payload: RedditiDaTerreni) {
        return this.service.post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-redditi-quadro-ra`, payload).pipe(
            tap(() => {
                this.refreshDichiarazioneDeiRedditi();
                this.notificaQuadroSalvato();
            }),
        );
    }

    upsertRedditiQuadroRb(utentiId: number, anno: number, redditiDaFabbricatiPayload: RedditiDaFabbricati) {
        return this.service
            .post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-redditi-quadro-rb`, redditiDaFabbricatiPayload)
            .pipe(
                tap(() => {
                    this.refreshDichiarazioneDeiRedditi();
                    this.notificaQuadroSalvato();
                }),
            );
    }

    upsertFamiliariACarico(utentiId: number, anno: number, familiariACarico: FamiliariACarico[]) {
        return this.service.post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-familiari`, familiariACarico).pipe(
            tap(() => {
                this.refreshDichiarazioneDeiRedditi();
                this.notificaQuadroSalvato();
            }),
        );
    }

    editQuadroRc(utentiId: number, anno: number, redditiDaDipendente: RedditiDaDipendente[]) {
        return this.service.post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-quadro-rc`, redditiDaDipendente).pipe(
            tap(() => {
                this.refreshDichiarazioneDeiRedditi();
                this.notificaQuadroSalvato();
            }),
        );
    }

    editQuadroRp(utentiId: number, anno: number, oneriSpese: OneriESpese[]) {
        return this.service.post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-quadro-rp`, oneriSpese).pipe(
            tap(() => {
                this.refreshDichiarazioneDeiRedditi();
                this.notificaQuadroSalvato();
            }),
        );
    }

    upsertRedditiQuadroRw(utentiId: number, anno: number, redditiFinanziariEsteri: RedditoFinanziarioEstero[]) {
        return this.service
            .post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/upsert-redditi-quadro-rw`, redditiFinanziariEsteri)
            .pipe(
                tap(() => {
                    this.refreshDichiarazioneDeiRedditi();
                    this.notificaQuadroSalvato();
                }),
            );
    }

    sbloccaQuadro(utentiId: number, anno: number, quadro: 'ra' | 'rb' | 'rc' | 'rp' | 'rw' | 'fa') {
        return this.service
            .post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/sblocca-quadro/${quadro}`, {})
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    getPdfOneriAndSpese(codiceFiscale: string, anno: number): Observable<{ fileName: string; blob: Blob }> {
        var params = new HttpParams().append('v', Date.now().toString()); // Per evitare la cache del browser
        return this.service
            .get<HttpResponse<ArrayBuffer>>({
                observe: 'response',
                url: `/backoffice/dichiarazioni/forfettari/${codiceFiscale}/dichiarazione/${anno}/pdf-oneri-and-spese`,
                params,
                responseType: 'blob' as 'json',
            })
            .pipe(
                map(response => {
                    const fileName = response.headers.get('content-disposition').split(';')[1].split('=')[1].replace(/\"/g, '');
                    const blob = new Blob([response.body], { type: 'application/pdf' });
                    return { fileName, blob };
                }),
            );
    }

    /* -------------------------------------------------------------------------- */
    /*                             Open Modal Methods                             */
    /* -------------------------------------------------------------------------- */
    openDettagliUnicoAnnoPrecedente(statoDichiarazione: StatoDichiarazione, cellId: string, dataToSendDichiarazione: DataToSendDichiarazione) {
        return this.dialog.open(DettagliQuadroLm45Component, {
            panelClass: 'toolbar-dialog',
            maxWidth: '40%',
            data: { statoDichiarazione, cellId, dataToSendDichiarazione },
        });
    }

    openDettagliContributiVersati(dataToSendDichiarazione: DataToSendDichiarazione) {
        return this.dialog.open(DettagliContributiVersatiComponent, {
            maxWidth: '40%',
            panelClass: 'toolbar-dialog',
            data: { dataToSendDichiarazione },
        });
    }

    openDettagliContributiVersatiDaF24(dataToSendDichiarazione: DataToSendDichiarazione) {
        return this.dialog.open(DettagliContributiVersatiDaF24Component, {
            panelClass: 'toolbar-dialog',
            maxWidth: '60%',
            data: { dataToSendDichiarazione },
        });
    }

    openDettagliCreditoAnnoPrecedenteCompensatoModF24(cellId: string, dataToSendDichiarazione: DataToSendDichiarazione) {
        return this.dialog.open(DettagliCreditoAnnoPrecCompensatoModF24Component, {
            panelClass: 'toolbar-dialog',
            maxWidth: '60%',
            data: { cellId, dataToSendDichiarazione },
        });
    }

    openDettagliQuadroRs(statoDichiarazione: StatoDichiarazione, rigoRsId: number, cellId: string, dataToSendDichiarazione: DataToSendDichiarazione) {
        return this.dialog.open(DettagliQuadroRsComponent, {
            ...this.commonDialogConfig,
            data: { statoDichiarazione, rigoRsId, cellId, dataToSendDichiarazione },
        });
    }

    openDettagliAccontiVersati(cellId: string, dataToSendDichiarazione: DataToSendDichiarazione) {
        this.commonDialogConfig.width = '80%';
        return this.dialog.open(DettagliAccontoVersatoComponent, {
            ...this.commonDialogConfig,
            data: { cellId, dataToSendDichiarazione },
        });
    }

    upsertIgnoraAccontiAnnoPrecedenteNonPagati(utentiId: number, anno: number, ignoraAccontiAnnoPrecedenteNonPagati: boolean) {
        const params = new HttpParams().set('ignoraAccontiAnnoPrecedenteNonPagati', ignoraAccontiAnnoPrecedenteNonPagati.toString());
        return this.service
            .post(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/ignora-acconti-anno-precedente-non-pagati`, null, params)
            .pipe(tap(() => this.refreshDichiarazioneDeiRedditi()));
    }

    getIgnoraAccontiAnnoPrecedenteNonPagati(utentiId: number, anno: number) {
        return this.service.get<boolean>(`/backoffice/dichiarazioni/forfettari/${utentiId}/dichiarazione/${anno}/ignora-acconti-anno-precedente-non-pagati`);
    }
}
